// =====================================================
// components/PostCard.tsx
// LikeButton + PostCard
// Ajout : onOpenSharers → SharersModal sur les partages
// =====================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, Text, View, TouchableOpacity,
  Animated, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { SafeImage, MediaViewer } from './MediaViewers';
import ShareButton from './ShareButton';
import {
  Post, NATIVE, LIKE_ACTIVE_COLOR, LIKE_INACTIVE_COLOR, FB_GRAY_COLOR,
  formatTimeAgo, getInitials,
} from '../lib/feedHelpers';

// ─── LikeButton ──────────────────────────────────────────────────────────────
export function LikeButton({ liked, isAnim, onPress }: {
  liked: boolean; isAnim: boolean; onPress: () => void;
}) {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim  = useRef(new Animated.Value(0)).current;

  const handlePress = () => {
    if (isAnim) return;
    Animated.parallel([
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.3, duration: 120, useNativeDriver: true }),
        Animated.spring(scaleAnim, { toValue: 1, friction: 4, tension: 50, useNativeDriver: true }),
      ]),
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: 120, useNativeDriver: false }),
        Animated.timing(glowAnim, { toValue: 0, duration: 400, useNativeDriver: false }),
      ]),
    ]).start();
    onPress();
  };

  const glowColor = glowAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['rgba(138,43,226,0)', 'rgba(138,43,226,0.18)'],
  });

  return (
    <TouchableOpacity style={styles.actionBtnModern} onPress={handlePress} disabled={isAnim} activeOpacity={0.7}>
      <Animated.View style={[
        styles.actionContentModern,
        styles.likeBtnInner,
        liked && styles.likeBtnActive,
        { backgroundColor: glowColor as any },
      ]}>
        <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
          <MaterialCommunityIcons
            name={liked ? 'thumb-up' : 'thumb-up-outline'}
            size={26}
            color={liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR}
          />
        </Animated.View>
      </Animated.View>
    </TouchableOpacity>
  );
}

// ─── PostCard ─────────────────────────────────────────────────────────────────
export interface PostCardProps {
  post:            Post;
  userId:          string;
  isAdmin:         boolean;
  onLike:          (postId: string, liked: boolean)  => Promise<void>;
  onShare:         (postId: string, shared: boolean) => Promise<void>;
  onSave:          (postId: string, saved: boolean)  => Promise<void>;
  onReport:        (postId: string) => void;
  onAdminStrike:   (postId: string) => void;
  onOpenComments:  (postId: string) => void;
  onOpenLikers:    (postId: string) => void;
  onOpenSharers:   (postId: string) => void;   // ← NOUVEAU
  onOpenEdit:      (post: { id: string; content: string }) => void;
  onLongPress:     (post: Post) => void;
  onAuthorPress:   (authorId: string) => void;
}

const PostCard = React.memo(({
  post, userId, isAdmin, onLike, onShare, onSave, onReport, onAdminStrike,
  onOpenComments, onOpenLikers, onOpenSharers, onOpenEdit, onLongPress, onAuthorPress,
}: PostCardProps) => {
  const [liked,       setLiked]       = useState(post.user_liked  || false);
  const [shared,      setShared]      = useState(post.user_shared || false);
  const [saved,       setSaved]       = useState(post.user_saved  || false);
  const [likesCount,  setLikesCount]  = useState(post.reactions.likes);
  const [sharesCount, setSharesCount] = useState(post.reactions.shares);
  const [isAnim,      setIsAnim]      = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState<string | null>(null);
  const [viewerType,  setViewerType]  = useState<'image' | 'video'>('image');

  const isOwn = post.author_id === userId;

  useEffect(() => {
    setLiked(post.user_liked  || false);
    setShared(post.user_shared || false);
    setSaved(post.user_saved  || false);
    setLikesCount(post.reactions.likes);
    setSharesCount(post.reactions.shares);
  }, [post.id, post.user_liked, post.user_shared, post.user_saved, post.reactions]);

  const handleLike = async () => {
    if (isAnim) return;
    setIsAnim(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const n = !liked;
    setLiked(n); setLikesCount(p => n ? p + 1 : p - 1);
    try { await onLike(post.id, n); }
    catch { setLiked(!n); setLikesCount(p => n ? p - 1 : p + 1); }
    finally { setTimeout(() => setIsAnim(false), 300); }
  };

  const handleShareLocal = async (postId: string, sh: boolean) => {
    const n = sh;
    setShared(n); setSharesCount(p => n ? p + 1 : p - 1);
    try { await onShare(postId, n); }
    catch { setShared(!n); setSharesCount(p => n ? p - 1 : p + 1); }
  };

  const handleSave = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const n = !saved; setSaved(n);
    try { await onSave(post.id, n); }
    catch { setSaved(!n); }
  };

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />
      <TouchableOpacity style={styles.postCard} activeOpacity={1} onLongPress={() => onLongPress(post)}>

        {/* HEADER */}
        <View style={styles.postHeader}>
          <TouchableOpacity
            style={styles.postAuthor}
            onPress={() => { if (!isOwn) onAuthorPress(post.author_id); }}
            activeOpacity={isOwn ? 1 : 0.7}
          >
            <SafeImage
              uri={post.author.avatar_url}
              style={styles.avatar}
              resizeMode="cover"
              fallback={
                <View style={styles.avatarPlaceholder}>
                  <Text style={styles.avatarText}>{getInitials(post.author.nom, post.author.prenom)}</Text>
                </View>
              }
            />
            <View>
              <Text style={styles.authorName}>{post.author.prenom} {post.author.nom}</Text>
              <Text style={styles.postTime}>{formatTimeAgo(post.created_at)}</Text>
            </View>
          </TouchableOpacity>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
            {isAdmin && (
              <TouchableOpacity onPress={() => onAdminStrike(post.id)}>
                <Ionicons name="shield-half" size={20} color="#E74C3C" />
              </TouchableOpacity>
            )}
            {!isOwn && (
              <TouchableOpacity onPress={() => onReport(post.id)}>
                <Ionicons name="warning" size={20} color="#EF4444" />
              </TouchableOpacity>
            )}
            {isOwn && (
              <TouchableOpacity onPress={() => onOpenEdit({ id: post.id, content: post.content })}>
                <Ionicons name="ellipsis-horizontal" size={20} color="#666" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* CONTENU */}
        <Text style={styles.postContent}>{post.content}</Text>

        {post.imagepots ? (
          <TouchableOpacity activeOpacity={0.92} onPress={() => { setViewerType('image'); setViewerUrl(post.imagepots!); }}>
            <SafeImage uri={post.imagepots} style={styles.postImage} resizeMode="cover" fallback={null} />
            <View style={styles.mediaExpandHint}>
              <Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" />
            </View>
          </TouchableOpacity>
        ) : null}

        {post.vidposts ? (
          <TouchableOpacity style={styles.videoThumb} activeOpacity={0.92} onPress={() => { setViewerType('video'); setViewerUrl(post.vidposts!); }}>
            <Ionicons name="play-circle" size={48} color="rgba(255,255,255,0.9)" />
            <Text style={styles.videoThumbLabel}>Appuyer pour lire</Text>
          </TouchableOpacity>
        ) : null}

        {/* STATS BAR */}
        <View style={styles.statsBar}>
          <TouchableOpacity onPress={() => { if (likesCount > 0) onOpenLikers(post.id); }}>
            {likesCount > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View style={styles.statsLikeCircle}>
                  <MaterialCommunityIcons name="thumb-up" size={10} color="#FFF" />
                </View>
                <Text style={styles.statsText}>{likesCount}</Text>
              </View>
            ) : <Text style={styles.statsText}></Text>}
          </TouchableOpacity>
          <View style={styles.statsRight}>
            {post.reactions.comments > 0 && (
              <Text style={styles.statsText}>{post.reactions.comments} commentaires</Text>
            )}
            {post.reactions.comments > 0 && sharesCount > 0 && (
              <Text style={styles.statsSep}>•</Text>
            )}
            {/* ✅ Partages cliquables → SharersModal */}
            {sharesCount > 0 && (
              <TouchableOpacity onPress={() => onOpenSharers(post.id)}>
                <Text style={[styles.statsText, styles.statsSharesLink]}>
                  {sharesCount} partage{sharesCount > 1 ? 's' : ''}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* ACTIONS BAR */}
        <View style={styles.actionsBar}>
          <LikeButton liked={liked} isAnim={isAnim} onPress={handleLike} />

          <TouchableOpacity
            style={styles.actionBtnModern}
            onPress={() => {
              if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              onOpenComments(post.id);
            }}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name="chatbubble-outline" size={22} color={FB_GRAY_COLOR} />
              <Text style={styles.actionTextModern}>Commenter</Text>
            </View>
          </TouchableOpacity>

          {(post.visibility === 'public' || post.visibility === 'pro') && (
            <ShareButton
              postId={post.id}
              content={post.content}
              isPro={post.visibility === 'pro'}
              shared={shared}
              sharesCount={sharesCount}
              onShareHarmonia={handleShareLocal}
            />
          )}

          <TouchableOpacity
            style={[styles.actionBtnModern, { flex: 0, paddingHorizontal: 12 }]}
            onPress={handleSave}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name={saved ? 'bookmark' : 'bookmark-outline'} size={22} color={saved ? '#FFD700' : FB_GRAY_COLOR} />
            </View>
          </TouchableOpacity>
        </View>

      </TouchableOpacity>
    </>
  );
});

export default PostCard;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  postCard:          { backgroundColor: '#fff', marginHorizontal: 0, marginVertical: 4, paddingVertical: 14 },
  postHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, marginBottom: 10 },
  postAuthor:        { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  avatar:            { width: 40, height: 40, borderRadius: 20 },
  avatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  authorName:        { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  postTime:          { fontSize: 12, color: '#65676B', marginTop: 1 },
  postContent:       { fontSize: 15, color: '#050505', lineHeight: 22, paddingHorizontal: 14, marginBottom: 10 },
  postImage:         { width: '100%', height: 300, marginBottom: 10 },
  mediaExpandHint:   { position: 'absolute', bottom: 18, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 4 },
  videoThumb:        { width: '100%', height: 240, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', marginBottom: 10, gap: 6 },
  videoThumbLabel:   { color: 'rgba(255,255,255,0.75)', fontSize: 13 },

  statsBar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  statsLikeCircle: { backgroundColor: '#8A2BE2', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  statsText:       { fontSize: 13, color: '#65676B' },
  statsSharesLink: { color: '#8A2BE2', fontWeight: '600' },
  statsRight:      { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statsSep:        { color: '#CCC' },

  actionsBar:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#CED0D4', paddingTop: 4, paddingBottom: 4, marginHorizontal: 12, marginTop: 4 },
  actionBtnModern:     { flex: 1, paddingVertical: 8, borderRadius: 8 },
  actionContentModern: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  actionTextModern:    { fontSize: 14, fontWeight: '600', color: FB_GRAY_COLOR },
  likeBtnInner:        { borderRadius: 8, paddingVertical: 4, paddingHorizontal: 8 },
  likeBtnActive:       { shadowColor: '#8A2BE2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 8, elevation: 4 },
});

